import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, IconButton, Typography, Chip, TextField,
  InputAdornment, CircularProgress, Stack, Card, CardContent,
  Divider, alpha, useTheme, Tabs, Tab, Badge, Avatar, Menu,
  MenuItem, Fade, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, PostAdd as PostIcon, Undo as UndoIcon,
  Receipt as ReceiptIcon, TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon, CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon, MoreVert as MoreVertIcon,
  Visibility as ViewIcon, LocalPizza as BakeryIcon,
  ShoppingCart as ShoppingCartIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import PurchaseInvoiceDialog from '../components/purchaseInvoices/PurchaseInvoiceDialog';
import PurchaseInvoicePreviewDialog from '../components/purchaseInvoices/PurchaseInvoicePreviewDialog';
import { PurchaseInvoice } from '../types/purchaseInvoices.types';
import { purchaseInvoicesService } from '../services/purchaseInvoices.service';
import { branchesService } from '../services/branches.service';
import axiosInstance from '../services/axiosInstance';

const tr = {
  en: {
    title: 'Bakery Purchases', subtitle: 'Raw material purchases for bakery warehouse',
    addInvoice: 'New Invoice', search: 'Search invoices...',
    all: 'All', draft: 'Draft', posted: 'Posted',
    totalInvoices: 'Total Invoices', totalAmount: 'Total Amount', unpaidAmount: 'Unpaid',
    supplier: 'Supplier', warehouse: 'Warehouse', date: 'Date',
    paymentSource: 'Payment Source', custody: 'Custody', paymentAccount: 'Account',
    amount: 'Amount', paid: 'Paid', balance: 'Balance',
    status: 'Status', actions: 'Actions',
    view: 'View', edit: 'Edit', delete: 'Delete',
    post: 'Post Invoice', unpost: 'Unpost', noData: 'No invoices found',
    confirmDelete: 'Delete this invoice?',
    confirmPost: 'Post this invoice? Stock and accounting will be affected.',
    confirmUnpost: 'Unpost this invoice? This will reverse all effects.',
    journalEntry: 'J.E.',
  },
  ar: {
    title: 'مشتريات المخبز', subtitle: 'شراء المواد الخام لمخزن المخبز',
    addInvoice: 'فاتورة جديدة', search: 'بحث في الفواتير...',
    all: 'الكل', draft: 'مسودة', posted: 'مرحّل',
    totalInvoices: 'إجمالي الفواتير', totalAmount: 'إجمالي المبالغ', unpaidAmount: 'غير مدفوع',
    supplier: 'المورد', warehouse: 'المخزن', date: 'التاريخ',
    paymentSource: 'مصدر الدفع', custody: 'عهدة', paymentAccount: 'حساب مباشر',
    amount: 'المبلغ', paid: 'المدفوع', balance: 'المتبقي',
    status: 'الحالة', actions: 'الإجراءات',
    view: 'عرض', edit: 'تعديل', delete: 'حذف',
    post: 'ترحيل', unpost: 'إلغاء ترحيل', noData: 'لا توجد فواتير',
    confirmDelete: 'حذف هذه الفاتورة؟',
    confirmPost: 'ترحيل الفاتورة؟ سيتم التأثير على المخزون والمحاسبة.',
    confirmUnpost: 'إلغاء الترحيل؟ سيتم عكس جميع التأثيرات.',
    journalEntry: 'ق.م.',
  },
};

const BROWN = '#5d4037';
const LIGHT_BROWN = '#8d6e63';

const BakeryPurchasePage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = tr[language as 'en' | 'ar'];
  const isRTL = language === 'ar';
  const isWarehouseKeeper = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !!payload.KeeperWarehouseId;
    } catch { return false; }
  })();

  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [filtered, setFiltered] = useState<PurchaseInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<PurchaseInvoice | undefined>();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuInvoice, setMenuInvoice] = useState<PurchaseInvoice | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState<PurchaseInvoice | null>(null);
  const [warehouseBranchWarehouseId, setWarehouseBranchWarehouseId] = useState<number | null>(null);

  useEffect(() => { loadWarehouseBranchThenInvoices(); }, []);

  useEffect(() => {
    let data = invoices;
    if (activeTab === 1) data = data.filter(i => i.status === 'Draft');
    else if (activeTab === 2) data = data.filter(i => i.status === 'Posted');
    if (search.trim()) {
      const s = search.toLowerCase();
      data = data.filter(i =>
        i.invoiceNumber.toLowerCase().includes(s) ||
        (i.supplierName || '').toLowerCase().includes(s) ||
        (i.supplierNameAr || '').toLowerCase().includes(s)
      );
    }
    setFiltered(data);
  }, [invoices, search, activeTab]);

  const loadWarehouseBranchThenInvoices = async () => {
    setLoading(true);
    try {
      const warehouseBranch = await branchesService.getWarehouseBranch();
      const wId = warehouseBranch?.warehouseId ?? null;
      setWarehouseBranchWarehouseId(wId);
      if (wId) {
        const res = await axiosInstance.get(`/PurchaseInvoices?warehouseId=${wId}`);
        const list = (res.data || []).sort((a: PurchaseInvoice, b: PurchaseInvoice) => b.purchaseInvoiceId - a.purchaseInvoiceId);
        setInvoices(list);
      }
    } catch { toast.error(isRTL ? 'فشل تحميل الفواتير' : 'Failed to load invoices'); }
    finally { setLoading(false); }
  };

  const loadInvoices = async () => {
    setLoading(true);
    try {
      if (warehouseBranchWarehouseId) {
        const res = await axiosInstance.get(`/PurchaseInvoices?warehouseId=${warehouseBranchWarehouseId}`);
        const list = (res.data || []).sort((a: PurchaseInvoice, b: PurchaseInvoice) => b.purchaseInvoiceId - a.purchaseInvoiceId);
        setInvoices(list);
      }
    } catch { toast.error(isRTL ? 'فشل تحميل الفواتير' : 'Failed to load invoices'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    try {
      await purchaseInvoicesService.delete(id);
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
      loadInvoices();
    } catch (err: any) { toast.error(err.response?.data?.message || err.message); }
    handleCloseMenu();
  };

  const handlePost = async (id: number) => {
    try {
      await purchaseInvoicesService.postInvoice(id);
      toast.success(isRTL ? 'تم الترحيل بنجاح' : 'Posted successfully');
      loadInvoices();
    } catch (err: any) { toast.error(err.response?.data?.message || err.message); }
    handleCloseMenu();
  };

  const handleUnpost = async (id: number) => {
    try {
      await purchaseInvoicesService.unpostInvoice(id);
      toast.success(isRTL ? 'تم إلغاء الترحيل' : 'Unposted');
      loadInvoices();
    } catch (err: any) { toast.error(err.response?.data?.message || err.message); }
    handleCloseMenu();
  };

  const handleView = async (inv: PurchaseInvoice) => {
    handleCloseMenu();
    try {
      const full = await purchaseInvoicesService.getById(inv.purchaseInvoiceId);
      setPreviewInvoice(full);
      setPreviewOpen(true);
    } catch { toast.error(isRTL ? 'فشل تحميل التفاصيل' : 'Failed to load details'); }
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, inv: PurchaseInvoice) => {
    setAnchorEl(e.currentTarget); setMenuInvoice(inv);
  };
  const handleCloseMenu = () => { setAnchorEl(null); setMenuInvoice(null); };

  const stats = {
    total: invoices.length,
    totalAmount: invoices.reduce((s, i) => s + (i.totalAmount || 0), 0),
    unpaid: invoices.reduce((s, i) => s + (i.remainingAmount || 0), 0),
    draft: invoices.filter(i => i.status === 'Draft').length,
    posted: invoices.filter(i => i.status === 'Posted').length,
  };

  const fmt = (d: string) => new Date(d).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 56, height: 56, background: `linear-gradient(135deg, ${BROWN} 0%, ${LIGHT_BROWN} 100%)` }}>
            <BakeryIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} color={BROWN}>{t.title}</Typography>
            <Typography variant="caption" color="text.secondary">{t.subtitle}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={loadInvoices} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedInvoice(undefined); setDialogOpen(true); }}
            size="large" sx={{ background: `linear-gradient(45deg, ${BROWN} 30%, ${LIGHT_BROWN} 90%)`, px: 4, py: 1.5 }}>
            {t.addInvoice}
          </Button>
        </Stack>
      </Stack>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 2, mb: 3 }}>
        {[
          { label: t.totalInvoices, value: stats.total, icon: <ShoppingCartIcon sx={{ fontSize: 50, opacity: 0.3 }} />, gradient: `linear-gradient(135deg, ${BROWN} 0%, ${LIGHT_BROWN} 100%)`, fmt: (v: number) => String(v) },
          { label: t.totalAmount, value: stats.totalAmount, icon: <TrendingUpIcon sx={{ fontSize: 50, opacity: 0.3 }} />, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', fmt: (v: number) => `${v.toFixed(3)} KD` },
          { label: t.unpaidAmount, value: stats.unpaid, icon: <MoneyIcon sx={{ fontSize: 50, opacity: 0.3 }} />, gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', fmt: (v: number) => `${v.toFixed(3)} KD` },
          { label: t.status, value: null, icon: null, gradient: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', fmt: () => '' },
        ].map((s, i) => (
          <Card key={i} sx={{ background: s.gradient, color: 'white', overflow: 'hidden', position: 'relative' }}>
            <CardContent>
              {s.value !== null ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>{s.label}</Typography>
                    <Typography variant={i === 0 ? 'h3' : 'h5'} fontWeight={700}>{s.fmt(s.value)}</Typography>
                  </Box>
                  {s.icon}
                </Stack>
              ) : (
                <>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>{s.label}</Typography>
                  <Stack direction="row" spacing={2} mt={0.5}>
                    <Box><Typography variant="h6" fontWeight={700}>{stats.draft}</Typography><Typography variant="caption">{t.draft}</Typography></Box>
                    <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                    <Box><Typography variant="h6" fontWeight={700}>{stats.posted}</Typography><Typography variant="caption">{t.posted}</Typography></Box>
                  </Stack>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Paper elevation={3} sx={{ mb: 3, p: 2, background: theme.palette.mode === 'dark' ? `linear-gradient(135deg, #3e2723 0%, #4e342e 100%)` : `linear-gradient(135deg, ${alpha(BROWN, 0.08)} 0%, ${alpha(LIGHT_BROWN, 0.12)} 100%)`, borderRadius: 3 }}>
        <Stack spacing={2}>
          <TextField fullWidth placeholder={t.search} value={search} onChange={e => setSearch(e.target.value)}
            sx={{ backgroundColor: 'background.paper', borderRadius: 2, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'transparent' } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: BROWN }} /></InputAdornment> }} />
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
            sx={{ '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontSize: '1rem', fontWeight: 600 }, '& .Mui-selected': { color: BROWN } }}>
            <Tab label={<Badge badgeContent={stats.total} color="primary" max={999}><span>{t.all}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.draft} color="warning" max={999}><span>{t.draft}</span></Badge>} />
            <Tab label={<Badge badgeContent={stats.posted} color="success" max={999}><span>{t.posted}</span></Badge>} />
          </Tabs>
        </Stack>
      </Paper>

      {/* List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={60} sx={{ color: BROWN }} /></Box>
      ) : (
        <Stack spacing={2}>
          {filtered.map((inv, idx) => (
            <Fade in timeout={300 + idx * 80} key={inv.purchaseInvoiceId}>
              <Card elevation={3} sx={{
                transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
                border: 2, borderColor: inv.status === 'Posted' ? 'success.light' : 'warning.light',
                borderLeft: 6, borderLeftColor: inv.status === 'Posted' ? 'success.main' : BROWN,
                background: theme.palette.mode === 'dark' ? alpha('#1e293b', 0.8) : '#ffffff',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ width: 50, height: 50, bgcolor: inv.status === 'Posted' ? 'success.main' : BROWN }}>
                        <ReceiptIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700} color={BROWN}>{inv.invoiceNumber}</Typography>
                        <Typography variant="body2" component="div" color="text.secondary">{fmt(inv.invoiceDate)}</Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={inv.status === 'Posted' ? t.posted : t.draft}
                        color={inv.status === 'Posted' ? 'success' : 'warning'}
                        icon={inv.status === 'Posted' ? <CheckCircleIcon /> : <ScheduleIcon />}
                        sx={{ fontWeight: 600, minWidth: 90 }} />
                      {inv.status === 'Posted' && inv.journalEntryId && (
                        <Chip label={`${t.journalEntry} ${inv.journalEntryNumber || 'JE'} (#${inv.journalEntryId})`} size="small" color="info" variant="outlined" />
                      )}
                      <IconButton onClick={e => handleOpenMenu(e, inv)}><MoreVertIcon /></IconButton>
                    </Stack>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' }, gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t.paymentSource}</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {inv.custodyId ? (
                          <Chip label={`${t.custody}: ${inv.custodyNumber} — ${inv.employeeName || ''}`} size="small" color="warning" variant="outlined" />
                        ) : inv.paymentAccountId ? (
                          <Chip label={`${t.paymentAccount}: ${inv.paymentAccountName || inv.paymentAccountCode}`} size="small" color="info" variant="outlined" />
                        ) : inv.supplierName ? (
                          <Chip label={`${t.supplier}: ${isRTL ? inv.supplierNameAr : inv.supplierName}`} size="small" color="primary" variant="outlined" />
                        ) : '-'}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t.warehouse}</Typography>
                      <Typography variant="body1" fontWeight={600}>{isRTL ? inv.warehouseNameAr : inv.warehouseName}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t.amount}</Typography>
                      <Typography variant="h6" fontWeight={700} color={BROWN}>{inv.totalAmount?.toFixed(3)} KD</Typography>
                    </Box>
                    <Box>
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">{t.paid}:</Typography>
                          <Typography variant="body2" color="success.main" fontWeight={600}>{inv.paidAmount?.toFixed(3)} KD</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">{t.balance}:</Typography>
                          <Typography variant="body2" color="error.main" fontWeight={700}>{inv.remainingAmount?.toFixed(3)} KD</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          ))}
          {filtered.length === 0 && (
            <Paper sx={{ p: 8, textAlign: 'center', bgcolor: alpha(BROWN, 0.04) }}>
              <BakeryIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
            </Paper>
          )}
        </Stack>
      )}

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}
        PaperProps={{ elevation: 3, sx: { minWidth: 200, mt: 1 } }}>
        <MenuItem onClick={() => menuInvoice && handleView(menuInvoice)}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
          <Typography color="primary.main">{t.view}</Typography>
        </MenuItem>
        <Divider />
        {menuInvoice?.status === 'Draft' && (
          <MenuItem onClick={() => { setSelectedInvoice(menuInvoice!); setDialogOpen(true); handleCloseMenu(); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />{t.edit}
          </MenuItem>
        )}
        {menuInvoice?.status === 'Draft' && (
          <MenuItem onClick={() => handleDelete(menuInvoice!.purchaseInvoiceId)}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
            <Typography color="error">{t.delete}</Typography>
          </MenuItem>
        )}
        {menuInvoice?.status === 'Draft' && !isWarehouseKeeper && (
          <MenuItem onClick={() => handlePost(menuInvoice!.purchaseInvoiceId)}>
            <PostIcon fontSize="small" sx={{ mr: 1 }} color="success" />
            <Typography color="success.main">{t.post}</Typography>
          </MenuItem>
        )}
        {menuInvoice?.status === 'Posted' && !isWarehouseKeeper && (
          <MenuItem onClick={() => handleUnpost(menuInvoice!.purchaseInvoiceId)}>
            <UndoIcon fontSize="small" sx={{ mr: 1 }} color="warning" />
            <Typography color="warning.main">{t.unpost}</Typography>
          </MenuItem>
        )}
      </Menu>

      <PurchaseInvoiceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        purchaseInvoice={selectedInvoice}
        onSave={loadInvoices}
        fixedWarehouseId={warehouseBranchWarehouseId ?? undefined}
        bakeryPurchaseOnly={true}
      />

      <PurchaseInvoicePreviewDialog
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewInvoice(null); }}
        invoice={previewInvoice}
        onEdit={() => { if (previewInvoice) { setPreviewOpen(false); setSelectedInvoice(previewInvoice); setDialogOpen(true); } }}
      />
    </Box>
  );
};

export default BakeryPurchasePage;
